import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import {
  archiveConversation,
  closeChat,
  consumeAutoRecord,
  decideProposal,
  getChatState,
  isTouchDevice,
  requestAutoRecord,
  loadConversation,
  newConversation,
  refreshConversations,
  renameConversation,
  sendMessage,
  setBubbleHidden,
  setEffort,
  setModel,
  subscribe,
  toggleChat,
  EFFORT_OPTIONS,
  MODEL_OPTIONS,
} from '../chat'
import { navigate } from '../router'
import { pushError, pushToast } from '../toast'
import { recordingSupported, startRecording, type Recording } from '../recorder'
import type { ChatEffort, ChatTurn, Proposal } from '../chat'

function useChat() {
  return useSyncExternalStore(subscribe, getChatState, getChatState)
}

// ── shared pieces ───────────────────────────────────────────────────────────

function Transcript({ turns, error }: { turns: ChatTurn[]; error: string | null }) {
  const endRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' })
  }, [turns])

  return (
    <div className="flex-1 overflow-y-auto px-4 py-3">
      {turns.length === 0 && !error && (
        <p className="py-8 text-center text-sm text-slate-400">
          Ask about your hours, to-dos, or notes.
        </p>
      )}
      <ul className="flex flex-col gap-4">
        {turns.map((t, i) => (
          <li key={i} className={t.role === 'user' ? 'flex justify-end' : ''}>
            <div
              className={
                t.role === 'user'
                  ? 'max-w-[85%] rounded-lg bg-slate-800 px-3 py-2 text-sm text-white'
                  : 'w-full text-sm text-slate-800'
              }
            >
              {t.tools.length > 0 && (
                <div className="mb-1 flex flex-wrap gap-1">
                  {t.tools.map((name, j) => (
                    <span key={j} className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-500">
                      {name.replace(/_/g, ' ')}
                    </span>
                  ))}
                </div>
              )}
              <div className="whitespace-pre-wrap">{t.text}</div>
              {t.streaming && t.text === '' && <span className="text-slate-400">Thinking…</span>}
              {t.proposals.map((p) => (
                <ProposalCard key={p.tool_use_id} proposal={p} />
              ))}
              {!t.complete && !t.streaming && t.proposals.length === 0 && (
                <div className="mt-1 text-xs text-amber-700">Response was interrupted.</div>
              )}
              {t.costUsd != null && t.costUsd > 0 && (
                <div className="mt-1 text-[10px] text-slate-300" title="Estimated cost of this turn at list price">
                  {t.costUsd < 0.01 ? `<1¢` : `${(t.costUsd * 100).toFixed(1)}¢`}
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
      {error && (
        <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
      <div ref={endRef} />
    </div>
  )
}

// A write the model wants to make. Nothing has happened server-side yet.
function ProposalCard({ proposal }: { proposal: Proposal }) {
  const label = proposal.name.replace(/_/g, ' ')
  if (proposal.decision) {
    return (
      <div className="mt-2 rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs text-slate-500">
        {proposal.decision === 'approved' ? '✓ Confirmed' : '✕ Discarded'} · {label}: {proposal.summary}
      </div>
    )
  }
  return (
    <div className="mt-2 rounded border border-[var(--accent)] bg-slate-50 px-3 py-2">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 text-sm text-slate-800">{proposal.summary}</div>
      <div className="mt-2 flex gap-2">
        <button
          className="rounded bg-slate-900 px-2.5 py-1 text-xs font-medium text-white hover:bg-slate-700"
          onClick={() => decideProposal(proposal.tool_use_id, true)}
        >
          Confirm
        </button>
        <button
          className="rounded border border-slate-300 px-2.5 py-1 text-xs text-slate-600 hover:bg-white"
          onClick={() => decideProposal(proposal.tool_use_id, false)}
        >
          Discard
        </button>
      </div>
    </div>
  )
}

// Model and effort, sticky across messages. Effort is the dominant cost lever
// — thinking tokens bill as output — so it sits next to the send button rather
// than buried in settings.
function ModelControls() {
  const st = useChat()
  const model = MODEL_OPTIONS.find((m) => m.id === st.model) ?? MODEL_OPTIONS[0]

  return (
    <div className="flex min-w-0 items-center gap-1 text-[11px] text-slate-400">
      <select
        className="max-w-[7.5rem] rounded border border-slate-200 bg-white px-1 py-0.5 text-[11px] text-slate-600"
        value={st.model}
        onChange={(e) => setModel(e.target.value)}
        title={`${model.note}\n$${model.input}/$${model.output} per million tokens in/out.\nSwitching models starts a fresh prompt cache.`}
      >
        {MODEL_OPTIONS.map((m) => (
          <option key={m.id} value={m.id}>
            {m.label}
          </option>
        ))}
      </select>
      <select
        className="rounded border border-slate-200 bg-white px-1 py-0.5 text-[11px] text-slate-600 disabled:opacity-40"
        value={st.effort}
        disabled={!model.modern}
        onChange={(e) => setEffort(e.target.value as ChatEffort)}
        title={
          model.modern
            ? 'How hard the model thinks before answering. Thinking bills as output, so this is the main cost dial.'
            : `${model.label} has no effort control.`
        }
      >
        {EFFORT_OPTIONS.map((e) => (
          <option key={e} value={e}>
            {e}
          </option>
        ))}
      </select>
    </div>
  )
}

function Composer({ disabled }: { disabled: boolean }) {
  // The textarea is deliberately UNCONTROLLED. A controlled value fights
  // Android's keyboard dictation: voice input arrives as composition events,
  // React re-sets `value` mid-composition, and the IME re-inserts what it had
  // already inserted — which reads as stuttered, duplicated words. Owning the
  // value via ref means React never writes into an in-flight composition.
  // `hasText` exists only to enable the send button.
  const [hasText, setHasText] = useState(false)
  const [recording, setRecording] = useState(false)
  const [elapsed, setElapsed] = useState(0)
  const [transcribing, setTranscribing] = useState(false)
  const ref = useRef<HTMLTextAreaElement>(null)
  const rec = useRef<Recording | null>(null)

  useEffect(() => () => rec.current?.cancel(), [])

  // Opening a fresh thread on a phone starts recording immediately. If the
  // browser refuses (permission, or a lost user-gesture context on iOS), the
  // panel just opens normally and the error surfaces — never a dead mic button.
  const st = useChat()
  useEffect(() => {
    if (st.autoRecord && consumeAutoRecord()) void toggleMic()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [st.autoRecord])

  const write = (value: string) => {
    const el = ref.current
    if (!el) return
    el.value = value
    setHasText(value.trim().length > 0)
  }

  function submit() {
    const t = ref.current?.value ?? ''
    if (!t.trim()) return
    write('')
    void sendMessage(t)
  }

  async function finishRecording() {
    const r = rec.current
    if (!r) return
    rec.current = null
    setRecording(false)
    setTranscribing(true)
    try {
      const text = await r.stop()
      if (text) {
        // Append to whatever is already there rather than replacing it.
        const existing = ref.current?.value ?? ''
        write(existing ? `${existing.trimEnd()} ${text}` : text)
        ref.current?.focus()
      } else {
        pushError("Didn't catch any speech.")
      }
    } catch (e) {
      pushError(`Transcription failed: ${(e as Error).message}`)
    } finally {
      setTranscribing(false)
    }
  }

  async function toggleMic() {
    if (recording) {
      void finishRecording()
      return
    }
    if (!recordingSupported()) {
      pushError('This browser cannot record audio.')
      return
    }
    try {
      setElapsed(0)
      rec.current = await startRecording({
        onTick: setElapsed,
        onAutoStop: () => void finishRecording(),
      })
      setRecording(true)
    } catch (e) {
      // Almost always a denied mic permission.
      pushError(`Couldn't start recording: ${(e as Error).message}`)
    }
  }

  const mmss = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, '0')}`

  return (
    <div className="border-t border-slate-200 p-3">
      <textarea
        ref={ref}
        rows={2}
        className="w-full resize-none rounded-md border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
        placeholder={
          recording ? 'Recording…' : transcribing ? 'Transcribing…' : 'Ask something, or dictate a note…'
        }
        defaultValue=""
        disabled={disabled}
        onInput={(e) => setHasText((e.target as HTMLTextAreaElement).value.trim().length > 0)}
        onKeyDown={(e) => {
          // Never submit mid-composition — on Android, Enter during an active
          // IME/voice composition would send a half-formed phrase.
          if (e.key === 'Enter' && !e.shiftKey && !(e.nativeEvent as any).isComposing) {
            e.preventDefault()
            submit()
          }
        }}
      />
      <div className="mt-1 flex items-center justify-between gap-2">
        {recording ? (
          <span className="flex items-center gap-1.5 text-[11px] font-medium text-red-600">
            <span className="h-2 w-2 rounded-full bg-red-600" />
            {mmss}
          </span>
        ) : (
          <ModelControls />
        )}
        <div className="flex items-center gap-1">
          {recordingSupported() && (
            <button
              className={
                'rounded-md px-2 py-1 disabled:opacity-40 ' +
                (recording ? 'bg-red-600 text-white hover:bg-red-500' : 'text-slate-500 hover:bg-slate-100')
              }
              title={recording ? 'Stop and transcribe' : 'Record a voice note'}
              aria-label={recording ? 'Stop and transcribe' : 'Record a voice note'}
              disabled={transcribing}
              onClick={toggleMic}
            >
              {recording ? (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <rect x="4" y="4" width="8" height="8" rx="1" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <rect x="6" y="1.5" width="4" height="8" rx="2" />
                  <path d="M3.5 7.5a4.5 4.5 0 0 0 9 0M8 12v2.5" />
                </svg>
              )}
            </button>
          )}
          <button
            className="rounded-md bg-slate-900 px-3 py-1 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-40"
            onClick={submit}
            disabled={disabled || !hasText}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  )
}

// ── launcher + panel (every screen) ─────────────────────────────────────────

// The floating launcher. Dismissable — hover reveals an ✕ on desktop, and a
// long press does the same on touch, where there is no hover.
function Bubble() {
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longPressed = useRef(false)

  const hide = () => {
    setBubbleHidden(true)
    pushToast('Chat bubble hidden — Ctrl+K still opens it', {
      actionLabel: 'Undo',
      onAction: () => setBubbleHidden(false),
    })
  }

  const startPress = () => {
    longPressed.current = false
    pressTimer.current = setTimeout(() => {
      longPressed.current = true
      hide()
    }, 550)
  }
  const endPress = () => {
    if (pressTimer.current) clearTimeout(pressTimer.current)
    pressTimer.current = null
  }

  useEffect(() => endPress, [])

  const open = () => {
    // A long press already handled this gesture; don't also open the panel.
    if (longPressed.current) {
      longPressed.current = false
      return
    }
    // On a phone, opening a fresh thread almost always means "I want to say
    // something", so go straight to recording. Reopening a thread that already
    // has history doesn't — you're reading it.
    const before = getChatState()
    const opening = !before.open
    toggleChat()
    if (opening && before.turns.length === 0 && isTouchDevice()) requestAutoRecord()
  }

  return (
    <div className="no-print group fixed bottom-5 right-5 z-40">
      <button
        className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-900 text-white shadow-lg hover:bg-slate-700"
        title="Assistant (Ctrl+K) — long press to hide"
        aria-label="Open assistant"
        onClick={open}
        onTouchStart={startPress}
        onTouchEnd={endPress}
        onTouchMove={endPress}
        onContextMenu={(e) => e.preventDefault()}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7">
          <path d="M3 15.5V5a1.5 1.5 0 0 1 1.5-1.5h11A1.5 1.5 0 0 1 17 5v7a1.5 1.5 0 0 1-1.5 1.5H7L3 17z" />
        </svg>
      </button>
      {/* Desktop affordance only — touch uses the long press above. */}
      <button
        className="absolute -right-1 -top-1 hidden h-5 w-5 items-center justify-center rounded-full border border-slate-300 bg-white text-xs text-slate-500 shadow hover:text-red-600 lg:group-hover:flex"
        title="Hide the chat bubble"
        aria-label="Hide the chat bubble"
        onClick={hide}
      >
        ✕
      </button>
    </div>
  )
}

export function ChatLauncher() {
  const st = useChat()

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.ctrlKey || e.metaKey
      if (mod && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault()
        toggleChat()
      }
      // Escape closes the panel, but must not steal Escape from grid cells,
      // which use it to revert an edit.
      if (e.key === 'Escape' && getChatState().open) {
        const el = document.activeElement
        const inPanel = el instanceof Node && document.getElementById('chat-panel')?.contains(el)
        if (inPanel || !(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement)) {
          closeChat()
        }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <>
      {!st.open && !st.bubbleHidden && <Bubble />}

      {st.open && (
        <div
          id="chat-panel"
          // Full-height sheet on phones; floating panel from sm up. The old
          // fixed 32rem height overflowed small screens.
          className="no-print fixed inset-x-2 bottom-2 top-16 z-40 flex flex-col rounded-lg border border-slate-200 bg-white shadow-2xl sm:inset-x-auto sm:bottom-5 sm:right-5 sm:top-auto sm:h-[32rem] sm:w-[26rem]"
        >
          <header className="flex items-center justify-between border-b border-slate-200 px-3 py-2">
            <span className="text-sm font-semibold text-slate-900">Assistant</span>
            <div className="flex items-center gap-1">
              <button
                className="rounded px-2 py-1 text-xs text-slate-500 hover:bg-slate-100"
                onClick={() => newConversation(st.clientId)}
              >
                New
              </button>
              <button
                className="rounded px-2 py-1 text-xs text-slate-500 hover:bg-slate-100"
                title="Open full view"
                onClick={() => {
                  closeChat()
                  navigate(st.conversationId ? `#/chat/${st.conversationId}` : '#/chat')
                }}
              >
                Expand
              </button>
              <button className="rounded px-2 py-1 text-slate-400 hover:bg-slate-100" onClick={closeChat} aria-label="Close">
                ✕
              </button>
            </div>
          </header>
          <Transcript turns={st.turns} error={st.error} />
          <Composer disabled={st.sending} />
        </div>
      )}
    </>
  )
}

// ── full view (#/chat) ──────────────────────────────────────────────────────

function ConversationList({ onPick }: { onPick?: () => void }) {
  const st = useChat()

  const rename = useCallback(async (id: number, current: string | null) => {
    const next = prompt('Conversation title', current ?? '')
    if (next != null) await renameConversation(id, next)
  }, [])

  return (
    <>
      <div className="mb-2 flex items-center justify-between gap-1">
        <h1 className="text-lg font-semibold tracking-tight text-slate-900">Chat</h1>
        <div className="flex items-center gap-1">
          {/* The way back after dismissing the launcher. */}
          <button
            className={
              'rounded p-1 ' +
              (st.bubbleHidden
                ? 'text-slate-400 hover:bg-slate-100 hover:text-slate-700'
                : 'bg-slate-100 text-[var(--accent)]')
            }
            title={st.bubbleHidden ? 'Show the chat bubble' : 'Hide the chat bubble'}
            aria-label={st.bubbleHidden ? 'Show the chat bubble' : 'Hide the chat bubble'}
            aria-pressed={!st.bubbleHidden}
            onClick={() => setBubbleHidden(!st.bubbleHidden)}
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7">
              <path d="M3 15.5V5a1.5 1.5 0 0 1 1.5-1.5h11A1.5 1.5 0 0 1 17 5v7a1.5 1.5 0 0 1-1.5 1.5H7L3 17z" />
              {st.bubbleHidden && <line x1="3.5" y1="16.5" x2="16.5" y2="3.5" />}
            </svg>
          </button>
          <button
            className="rounded-md bg-slate-900 px-2.5 py-1 text-xs font-medium text-white hover:bg-slate-700"
            onClick={() => {
              newConversation()
              navigate('#/chat')
              onPick?.()
            }}
          >
            New
          </button>
        </div>
      </div>
      <ul className="flex-1 overflow-y-auto rounded-lg border border-slate-200 bg-white">
        {st.conversations.length === 0 && (
          <li className="px-3 py-4 text-center text-sm text-slate-400">No conversations yet.</li>
        )}
        {st.conversations.map((cv) => (
          <li key={cv.id} className="group flex items-center border-b border-slate-100 last:border-0">
            <button
              className={
                'flex-1 truncate px-3 py-2 text-left text-sm ' +
                (cv.id === st.conversationId ? 'bg-slate-100 font-medium text-slate-900' : 'text-slate-600 hover:bg-slate-50')
              }
              onClick={() => {
                navigate(`#/chat/${cv.id}`)
                onPick?.()
              }}
            >
              {cv.title || 'Untitled'}
            </button>
            <button
              className="px-1 text-slate-300 hover:text-slate-600 lg:opacity-0 lg:group-hover:opacity-100"
              title="Rename"
              onClick={() => rename(cv.id, cv.title)}
            >
              ✎
            </button>
            <button
              className="px-2 text-slate-300 hover:text-red-500 lg:opacity-0 lg:group-hover:opacity-100"
              title="Archive"
              onClick={() => archiveConversation(cv.id)}
            >
              ✕
            </button>
          </li>
        ))}
      </ul>
    </>
  )
}

export default function ChatScreen({ conversationId }: { conversationId?: number }) {
  const st = useChat()
  const [listOpen, setListOpen] = useState(false) // mobile drawer
  const [sidebarOpen, setSidebarOpen] = useState(true) // desktop column

  useEffect(() => {
    void refreshConversations()
  }, [])

  useEffect(() => {
    if (conversationId && conversationId !== st.conversationId) void loadConversation(conversationId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId])

  return (
    <div className="mx-auto flex h-full max-w-6xl gap-6 px-4 py-5 sm:px-6 lg:px-8">
      {/* Desktop: collapsible column */}
      <aside className={'hidden shrink-0 flex-col lg:flex ' + (sidebarOpen ? 'w-64' : 'w-8')}>
        <div className={'mb-1 flex ' + (sidebarOpen ? 'justify-end' : 'justify-center')}>
          <button
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            title={sidebarOpen ? 'Collapse conversations' : 'Expand conversations'}
            aria-label={sidebarOpen ? 'Collapse conversations' : 'Expand conversations'}
            aria-expanded={sidebarOpen}
            onClick={() => setSidebarOpen((v) => !v)}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7">
              {sidebarOpen ? <path d="M10 3.5 5.5 8l4.5 4.5" /> : <path d="M6 3.5 10.5 8 6 12.5" />}
            </svg>
          </button>
        </div>
        {sidebarOpen && <ConversationList />}
      </aside>

      {/* Mobile: same list behind a drawer, since the column is collapsed */}
      {listOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setListOpen(false)}>
          <div className="absolute inset-0 bg-black/30" />
          <aside
            className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col bg-slate-50 p-3 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <ConversationList onPick={() => setListOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col rounded-lg border border-slate-200 bg-white">
        <div className="flex items-center gap-2 border-b border-slate-200 px-2 py-1.5 lg:hidden">
          <button
            className="rounded p-1.5 text-slate-500 hover:bg-slate-100"
            title="Conversations"
            aria-label="Conversations"
            onClick={() => setListOpen(true)}
          >
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
              <line x1="3" y1="5.5" x2="17" y2="5.5" />
              <line x1="3" y1="10" x2="17" y2="10" />
              <line x1="3" y1="14.5" x2="17" y2="14.5" />
            </svg>
          </button>
          <span className="truncate text-sm font-medium text-slate-700">
            {st.conversations.find((c) => c.id === st.conversationId)?.title || 'New conversation'}
          </span>
        </div>
        <Transcript turns={st.turns} error={st.error} />
        <Composer disabled={st.sending} />
      </div>
    </div>
  )
}
